import {
  type RiskGapStatus,
  riskGapDefaultSubstatuses,
} from '@ctw/shared/api/fhir/models/risk-gap-v2';
import { Button } from '@ctw/shared/components/button';
import { Heading } from '@ctw/shared/components/heading';
import { TagCheckboxGroup } from '@ctw/shared/components/inputs/tag-checkbox-group';
import { tw } from '@ctw/shared/utils/tailwind';
import { isEmpty } from 'lodash-es';
import { useState } from 'react';

/** User statuses that have dedicated dialogs in the form (Accept, Dismiss, Snooze/Pending). */
export type RiskGapStatusDialogStatus =
  | 'open-gap'
  | 'pending'
  | 'accepted-for-review'
  | 'dismissed';

const STATUS_DIALOG_COPY: Record<
  RiskGapStatusDialogStatus,
  { title: string; body: string; confirmLabel: string }
> = {
  'open-gap': {
    title: 'Reopen risk gap?',
    body: 'This will mark the risk gap as open again.',
    confirmLabel: 'Reopen',
  },
  pending: {
    title: 'Set to pending',
    body: 'This will mark the risk gap as pending for later review.',
    confirmLabel: 'Save',
  },
  'accepted-for-review': {
    title: 'Accept for review',
    body: 'This will mark the risk gap as accepted for review.',
    confirmLabel: 'Save',
  },
  dismissed: {
    title: 'Dismiss risk gap',
    body: 'This will mark the risk gap as dismissed.',
    confirmLabel: 'Save',
  },
};

export interface RiskGapStatusDialogContentProps {
  note: string;
  onConfirm: (substatuses: Array<string>, pendingNote: string) => void | Promise<void>;
  onCancel: () => void;
  status: RiskGapStatusDialogStatus;
}

export function RiskGapStatusDialogContent({
  status,
  note,
  onConfirm,
  onCancel,
}: RiskGapStatusDialogContentProps) {
  const [substatuses, setSubstatuses] = useState<Array<string>>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [pendingNote, setPendingNote] = useState(note);

  const copy = STATUS_DIALOG_COPY[status];
  const substatusOptions = riskGapDefaultSubstatuses[status as RiskGapStatus] ?? [];

  const handleConfirm = async () => {
    setIsSubmitting(true);
    try {
      await Promise.resolve(onConfirm(substatuses, pendingNote));
    } finally {
      setIsSubmitting(false);
    }
  };
  return (
    <div className={tw`w-[372px] space-y-6`}>
      <div className={tw`space-y-2`}>
        <Heading level="h2" className={tw`font-semibold text-lg`}>
          {copy.title}
        </Heading>
        <div className={tw`text-sm`}>{copy.body}</div>
      </div>

      {!isEmpty(substatusOptions) && (
        <TagCheckboxGroup
          legend="Details (select any that apply)"
          name={`risk-gap-status-dialog-substatus-${status}`}
          options={[...substatusOptions]}
          selectedOptions={substatuses}
          onOptionSelected={(option) => {
            if (!substatuses.includes(option.value)) {
              setSubstatuses((prev) => [...prev, option.value]);
            }
          }}
          onOptionDeselected={(option) => {
            setSubstatuses((prev) => prev.filter((v) => v !== option.value));
          }}
        />
      )}
      <textarea
        placeholder={'Add notes...'}
        onChange={(event) => setPendingNote(event.currentTarget.value)}
        value={pendingNote}
        rows={4}
        className={tw`w-full resize-none border border-meta-background shadow-none`}
      />
      <div className={tw`flex justify-end gap-3`}>
        <Button type="button" variant="secondary" onClick={onCancel} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          type="button"
          variant="primary"
          onClick={() => void handleConfirm()}
          disabled={isSubmitting}
        >
          {copy.confirmLabel}
        </Button>
      </div>
    </div>
  );
}
